import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FeatureFlags } from "@/api/methodologyConsole";
import { UI_EXPOSURE_FLAGS } from "@/config/compositionUiFlags";

const FlagRow = ({
  label,
  value,
  scope,
  note,
}: {
  label: string;
  value: boolean;
  scope: string;
  note?: string | null;
}) => (
  <div className="flex items-start justify-between gap-4 border-b py-2 last:border-0">
    <div className="min-w-0">
      <p className="text-sm font-medium">{label}</p>
      <p className="text-xs text-muted-foreground">{scope}</p>
      {note && <p className="mt-1 text-xs text-muted-foreground">{note}</p>}
    </div>
    <Badge variant={value ? "default" : "secondary"} aria-label={`${label}: ${value ? "on" : "off"}`}>
      {value ? "Enabled" : "Disabled"}
    </Badge>
  </div>
);

/**
 * Read-only feature state. Phase 3.5A deliberately provides no toggles:
 * operational flags are changed only through governed database procedures.
 */
export default function FeatureStatePanel({ flags }: { flags: FeatureFlags }) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Feature state (read-only)</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-3 text-xs text-muted-foreground">
          Operational flags are read from the database and cannot be changed from this
          console. UI exposure flags control visibility only and are never used as
          authorization.
        </p>
        <FlagRow
          label="runtime_enabled"
          value={flags.runtime_enabled}
          scope="Operational — Composition Runtime"
          note={flags.runtime_blocked_reason}
        />
        <FlagRow
          label="composition_enabled"
          value={flags.composition_enabled}
          scope="Operational — composed assessments"
        />
        <FlagRow
          label="applicability_enabled"
          value={flags.applicability_enabled}
          scope="Operational — applicability engine"
        />
        <FlagRow
          label="lineage_enabled"
          value={flags.lineage_enabled}
          scope="Operational — methodology lineage"
        />
        <FlagRow
          label="report_watermark_enabled"
          value={flags.report_watermark_enabled}
          scope="Operational — global watermark switch"
        />
        {UI_EXPOSURE_FLAGS.map((f) => (
          <FlagRow key={f.key} label={f.key} value={f.value} scope={f.scope} />
        ))}
      </CardContent>
    </Card>
  );
}
